import {
  faBed,
  faBoxesStacked,
  faBullseye,
  faClipboardQuestion,
  faFileText,
  faThumbTack,
  faUsersLine,
  faWandSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Button, Col, Container, Nav, Row, Spinner, Tab } from "react-bootstrap";
import PageTitle from "../ui/PageTitle";
import GoalsAndTasksTab from "./GoalsAndTasksTab";
import InteractionTab from "./InteractionTab";
import useApp from "../../utils/useApp";
import jsPDF from "jspdf";
import useMenuToggle from "../../utils/useMenuToggle";
import ShelterTab from "./ShelterTab";
import {
  fetchAllResources,
  fetchJobs,
  fetchOrganization,
  fetchPrograms,
  fetchReportCharts,
  fetchReportDashboardCharts,
} from "../../services/httpClient";
import Dashboard from "./Dashboard";
import { Tooltip } from "react-tooltip";
import ResourcesTab, { showResources } from "./ResourcesTab";
import ProgramsTab, { showPrograms } from "./ProgramsTab";
import JobsTab, { showJobs } from "./JobsTab";
import AssessmentsTab from "./AssessmentsTab";
import { signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";

export const activeKey = signal("interactions");

const Reports = () => {
  useSignals();
  const { auth, showMenu }: any = useApp();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const widthBasedToggle = useMenuToggle();
  const [toggleMenu, setToggleMenu] = useState(false);
  const [showCreateDashboard, setShowCreateDashboard] = useState<boolean>(true);
  const [profileImage, setProfileImage] = useState<string>("");
  const [isiPad, setIsiPad] = useState<boolean>(false);
  const [reportCharts, setReportCharts] = useState<any>();
  const [myDashboardCharts, setMyDashboardCharts] = useState<any[]>([]);

  //could be used later to order charts on dashboard based on which tab they come from
  const tabOrder = ["interactions", "goals and tasks", "assessments", "shelter", "programming", "job assignments", "resources"];

  const isSafariOniPad = () => {
    const ua = navigator.userAgent.toLowerCase();

    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

    const isSafari = /^((?!chrome|android).)*safari/.test(ua);
    const isiPad = /ipad/.test(ua) || (navigator.userAgent.includes("Macintosh") && isTouchDevice);

    return isiPad && isSafari;
  };

  useEffect(() => {
    getOrg();
    getPrograms();
    getJobs();
    getResources();
    getReportDashboardCharts();

    const isiPad = isSafariOniPad();
    setIsiPad(isiPad);
  }, []);

  useEffect(() => {
    setToggleMenu(!widthBasedToggle);
  }, [widthBasedToggle]);

  const getOrg = () => {
    fetchOrganization(auth?.profile?.userOrgID)
      .then((res: any) => {
        const currentOrg = res[0].data;

        if (currentOrg?.profile.length > 0) {
          currentOrg?.profile?.map((p: any) => {
            const base64String = "data:image/jpeg;base64, " + p?.data;
            setProfileImage(base64String);
          });
        } else {
          setProfileImage("");
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getJobs = () => {
    fetchJobs()
      .then((res: any) => {
        const data = res[0].data;
        data.length > 0 ? (showJobs.value = true) : (showJobs.value = false);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getPrograms = () => {
    fetchPrograms()
      .then((res: any) => {
        const data = res[0].data;
        data.length > 0 ? (showPrograms.value = true) : (showPrograms.value = false);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getResources = () => {
    fetchAllResources()
      .then((res: any) => {
        const data = res[0].data;
        data.length > 0 ? (showResources.value = true) : (showResources.value = false);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getReportCharts = (myCharts: any[]) => {
    fetchReportCharts()
      .then((res: any) => {
        var charts = res[0].data;
        //remove charts that are not available
        if (!showJobs.value) {
          charts = charts.filter((c: any) => c.key !== "job_table");
        }
        if (!showPrograms.value) {
          charts = charts.filter((c: any) => c.key !== "program_level_pie_chart" && c.key !== "program_table");
        }
        if (!showResources.value) {
          charts = charts.filter(
            (c: any) =>
              c.key !== "people_assigned_resources" &&
              c.key !== "consumables_assigned" &&
              c.key !== "nonconsumables_assigned" &&
              c.key !== "resource_usage_pct" &&
              c.key !== "consumables_assigned_table" &&
              c.key !== "nonconsumables_assigned_table",
          );
        }

        const myChartKeys = new Set(myCharts.map((m: any) => m.chart_key));

        const formatted = charts.map((c: any) => ({
          value: c.key,
          label: c.name,
          original: { ...c, is_existing: myChartKeys.has(c.key) },
          isDisabled: false,
          showNormal: true,
        }));

        const grouped = [...formatted].reduce((acc: any, item: any) => {
          const category = item?.original["category"] ?? "Uncategorized";

          if (!acc[category]) {
            acc[category] = [];
          }

          acc[category].push(item);
          return acc;
        }, {});

        const final = Object.entries(grouped).map(([label, options]) => ({
          label,
          options,
        }));

        const sorted = final.sort((a, b) => {
          return tabOrder.indexOf(a.label) - tabOrder.indexOf(b.label);
        });
        setReportCharts(sorted as any);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getReportDashboardCharts = () => {
    fetchReportDashboardCharts()
      .then((res: any) => {
        const myCharts = res[0].data;
        setMyDashboardCharts(myCharts);

        if (myCharts.length === 0 && activeKey.value !== "dashboard") {
          setShowCreateDashboard(true);
          activeKey.value = "interactions";
        } else {
          setShowCreateDashboard(false);
          activeKey.value = "dashboard";
        }

        getReportCharts(myCharts);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onCreateDashboardClick = () => {
    activeKey.value = "dashboard";
    setShowCreateDashboard(false);
  };

  const [canvas, setCanvas] = useState<any>();

  const getImageDimensions = (base64: string): Promise<{ width: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64;
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const targetWidth = 10 * aspectRatio;
        resolve({ width: targetWidth });
      };
    });
  };

  const buildPDF = async (type: string) => {
    try {
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const margin = 10;
      const headerHeight = 20; // Adjust as needed
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pdfWidth = pdf.internal.pageSize.getWidth() - 2 * margin;
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let yPosition = margin + headerHeight;

      // Add header to the first page
      if (profileImage) {
        const headerImg = new Image();
        headerImg.src = profileImage;
        const { width } = await getImageDimensions(profileImage);
        pdf.addImage(profileImage, "PNG", margin, margin, width, 10);
        pdf.setFontSize(12);
        pdf.text(type + " Report", margin + width + 5, margin + 7);
      } else {
        pdf.setFontSize(12);
        pdf.text(type + " Report", margin, margin + 7);
      }

      // Helper function to crop canvas
      const cropCanvas = (sourceCanvas: any, cropX: number, cropY: number, cropWidth: number, cropHeight: number) => {
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = cropWidth;
        tempCanvas.height = cropHeight;
        const ctx = tempCanvas.getContext("2d");
        ctx?.drawImage(sourceCanvas, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
        return tempCanvas;
      };

      // Check if the content fits on one page
      if (imgHeight <= pageHeight - yPosition) {
        pdf.addImage(imgData, "PNG", margin, yPosition, pdfWidth, imgHeight);
      } else {
        // Handle multi-page case
        let remainingHeight = canvas.height;
        let position = 0;
        const pageCanvasHeight = 1800;

        while (remainingHeight > 0) {
          const pageCanvas = cropCanvas(canvas, 0, position, canvas.width, pageCanvasHeight);
          const pageImgData = pageCanvas.toDataURL("image/png");
          pdf.addImage(pageImgData, "PNG", margin, yPosition, pdfWidth, pageCanvasHeight * (pdfWidth / canvas.width));

          remainingHeight -= pageCanvasHeight;
          position += pageCanvasHeight;

          if (remainingHeight > 0) {
            pdf.addPage();
            yPosition = margin + headerHeight;

            if (profileImage) {
              const headerImg = new Image();
              headerImg.src = profileImage;
              const { width } = await getImageDimensions(profileImage);
              pdf.addImage(profileImage, "PNG", margin, margin, width, 10);
              pdf.setFontSize(12);
              pdf.text(type.replaceAll(" ", "") + " Report", margin + width + 5, margin + 7);
            } else {
              pdf.setFontSize(12);
              pdf.text(type.replaceAll(" ", "") + " Report", margin, margin + 7);
            }
          }
        }
      }

      const formattedDate = new Date()
        .toISOString()
        .replace(/[-:.TZ]/g, "")
        .slice(0, 14);

      //trigger download
      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = type + "_" + formattedDate + ".pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error);
    }
  };

  return (
    <>
      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu custom-container" : "lg div-main custom-container"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <Container fluid="lg" className={showMenu ? "lg div-main-menu custom-container" : "lg div-main custom-container"}>
          <Tab.Container
            activeKey={activeKey.value}
            onSelect={(k) => {
              activeKey.value = k || "";
            }}
          >
            <Row>
              <Col sm={toggleMenu ? 1 : 2} style={toggleMenu ? { width: "50px", marginRight: "1rem" } : {}}>
                <Nav
                  variant="pills"
                  className={showMenu ? "flex-column sticky-top mycase-menu-open-sticky" : "flex-column sticky-top mycase-menu-sticky"}
                >
                  <Nav.Item className="mb-2">{!toggleMenu && <PageTitle title={"Reports"} type={"page"} />}</Nav.Item>
                  {!showCreateDashboard && (
                    <Nav.Item className="d-grid">
                      <Nav.Link eventKey="dashboard" className={activeKey.value === "dashboard" ? "nav-vertical-active" : "nav-vertical"}>
                        <>
                          <FontAwesomeIcon icon={faWandSparkles} className={toggleMenu ? "me-0" : "me-2"} />
                          {toggleMenu ? "" : "Dashboard"}
                        </>
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      eventKey="interactions"
                      className={activeKey.value === "interactions" ? "nav-vertical-active" : "nav-vertical"}
                    >
                      <div data-tooltip-id="tooltip-interactions" data-tooltip-content="Interactions">
                        <FontAwesomeIcon icon={faFileText} className={toggleMenu ? "me-0" : "me-2"} />
                        {toggleMenu ? "" : "Interactions"}
                        {toggleMenu && <Tooltip id="tooltip-interactions" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      eventKey="goals and tasks"
                      className={activeKey.value === "goals and tasks" ? "nav-vertical-active" : "nav-vertical"}
                    >
                      <div data-tooltip-id="tooltip-goals" data-tooltip-content="Goals">
                        <FontAwesomeIcon icon={faBullseye} className={toggleMenu ? "me-0" : "me-2"} />
                        {toggleMenu ? "" : "Goals and Tasks"}
                        {toggleMenu && <Tooltip id="tooltip-goals" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="d-grid">
                    <Nav.Link eventKey="assessments" className={activeKey.value === "assessments" ? "nav-vertical-active" : "nav-vertical"}>
                      <div data-tooltip-id="tooltip-assessments" data-tooltip-content="Assessments">
                        <FontAwesomeIcon icon={faClipboardQuestion} className={toggleMenu ? "me-0" : "me-2"} />
                        {toggleMenu ? "" : "Assessments"}
                        {toggleMenu && <Tooltip id="tooltip-assessments" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                  {auth?.profile.includeHMIS === 1 && (
                    <Nav.Item className="d-grid">
                      <Nav.Link eventKey="shelter" className={activeKey.value === "shelter" ? "nav-vertical-active" : "nav-vertical"}>
                        <div data-tooltip-id="tooltip-shelter" data-tooltip-content="Shelter">
                          <FontAwesomeIcon icon={faBed} className={toggleMenu ? "me-0" : "me-2"} />
                          {toggleMenu ? "" : "Shelter"}
                          {toggleMenu && <Tooltip id="tooltip-shelter" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                        </div>
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  {showPrograms.value && (
                    <Nav.Item className="d-grid">
                      <Nav.Link
                        eventKey="programming"
                        className={activeKey.value === "programming" ? "nav-vertical-active" : "nav-vertical"}
                      >
                        <div data-tooltip-id="tooltip-programs" data-tooltip-content="Programming">
                          <FontAwesomeIcon icon={faUsersLine} className={toggleMenu ? "me-0" : "me-2"} />
                          {toggleMenu ? "" : "Programming"}
                          {toggleMenu && <Tooltip id="tooltip-programs" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                        </div>
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  {showJobs.value && (
                    <Nav.Item className="d-grid">
                      <Nav.Link
                        eventKey="job assignments"
                        className={activeKey.value === "job assignments" ? "nav-vertical-active" : "nav-vertical"}
                      >
                        <div data-tooltip-id="tooltip-jobs" data-tooltip-content="Jobs">
                          <FontAwesomeIcon icon={faThumbTack} className={toggleMenu ? "me-0" : "me-2"} />
                          {toggleMenu ? "" : "Job Assignments"}
                          {toggleMenu && <Tooltip id="tooltip-jobs" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                        </div>
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  {showResources.value && (
                    <Nav.Item className="d-grid">
                      <Nav.Link eventKey="resources" className={activeKey.value === "resources" ? "nav-vertical-active" : "nav-vertical"}>
                        <div data-tooltip-id="tooltip-resources" data-tooltip-content="Resources">
                          <FontAwesomeIcon icon={faBoxesStacked} className={toggleMenu ? "me-0" : "me-2"} />
                          {toggleMenu ? "" : "Resources"}
                          {toggleMenu && <Tooltip id="tooltip-resources" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                        </div>
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  {showCreateDashboard && (
                    <Nav.Item className="d-grid">
                      <Button
                        data-testid="create-personal-dashboard"
                        className="app-primary-color-inverse mt-3"
                        onClick={() => onCreateDashboardClick()}
                      >
                        <FontAwesomeIcon icon={faWandSparkles} className={toggleMenu ? "me-0" : "me-2"} />
                        {toggleMenu ? "" : "Create Personal Dashboard"}
                      </Button>
                    </Nav.Item>
                  )}
                </Nav>
              </Col>
              <Col sm={toggleMenu ? 11 : 10} className="mt-2">
                <Tab.Content>
                  <Tab.Pane eventKey="dashboard">
                    {!showCreateDashboard && (
                      <Dashboard
                        setShowDashboard={setShowCreateDashboard}
                        myDashboardCharts={myDashboardCharts}
                        getMyDashboardCharts={getReportDashboardCharts}
                        reportCharts={reportCharts}
                        iPad={isiPad}
                        tabOrder={tabOrder}
                      />
                    )}
                  </Tab.Pane>
                  <Tab.Pane eventKey="interactions">
                    <InteractionTab
                      buildPDF={() => buildPDF("Interactions")}
                      canvas={canvas}
                      setCanvas={setCanvas}
                      iPad={isiPad}
                      isActive={activeKey.value === "interactions"}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="goals and tasks">
                    <GoalsAndTasksTab buildPDF={() => buildPDF("Goals/Tasks")} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
                  </Tab.Pane>
                  <Tab.Pane eventKey="assessments">
                    <AssessmentsTab buildPDF={(e: any) => buildPDF(e)} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
                  </Tab.Pane>
                  <Tab.Pane eventKey="shelter">
                    <ShelterTab
                      buildPDF={() => buildPDF("Shelter")}
                      canvas={canvas}
                      setCanvas={setCanvas}
                      iPad={isiPad}
                      isActive={activeKey.value === "shelter"}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="programming">
                    <ProgramsTab buildPDF={() => buildPDF("Programs")} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
                  </Tab.Pane>
                  <Tab.Pane eventKey="job assignments">
                    <JobsTab buildPDF={() => buildPDF("Jobs")} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
                  </Tab.Pane>
                  <Tab.Pane eventKey="resources">
                    <ResourcesTab buildPDF={() => buildPDF("Resources")} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </Container>
      )}
    </>
  );
};

export default Reports;
